import { Add, DeleteOutlined } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { RequirementFieldOption } from '../product-requirements-config.dto';

interface Props {
    value: RequirementFieldOption[];
    onChange: (value: RequirementFieldOption[]) => void;
}

/** Inline editor for a SELECT field's static `[{value,label}]` options. */
export default function OptionsEditor({ value, onChange }: Props) {
    const { t } = useTranslation();

    function update(index: number, patch: Partial<RequirementFieldOption>) {
        onChange(value.map((o, i) => (i === index ? { ...o, ...patch } : o)));
    }

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('Options')}
            </Typography>
            <Stack spacing={1}>
                {value.map((option, index) => (
                    <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <TextField
                            size="small"
                            label={t('Value')}
                            value={option.value}
                            onChange={(e) => update(index, { value: e.target.value })}
                        />
                        <TextField
                            size="small"
                            fullWidth
                            label={t('Label')}
                            value={option.label}
                            onChange={(e) => update(index, { label: e.target.value })}
                        />
                        <IconButton
                            size="small"
                            onClick={() => onChange(value.filter((_, i) => i !== index))}
                            aria-label={t('Remove option')}
                        >
                            <DeleteOutlined fontSize="small" />
                        </IconButton>
                    </Stack>
                ))}
                <Box>
                    <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() => onChange([...value, { value: '', label: '' }])}
                    >
                        {t('Add option')}
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
}
